/**
 * Links that are already out in the world.
 *
 * The JavaScript application served the two public documents as single static
 * files and carried the token in the hash, so every payment request and every
 * intake form a tutor has ever sent looks like this:
 *
 *     https://magbit.shop/pay/#Ab3-x9…
 *     https://magbit.shop/intake/#Ab3-x9…
 *
 * with `?t=` as an older variant. Those messages are in people's chat
 * histories and cannot be edited. `/pay/:token` is the shape this application
 * uses from now on, and these helpers are what keep the old ones working.
 *
 * The app also routed its own screens by hash (`/user/#payments`), so a
 * bookmark from the old app is translated to the path that answers it now.
 */

/** The token from `#token` or `?t=token`, in that order. Null when neither. */
export function tokenFromLocation(hash: string, search: string): string | null {
  const fromHash = hash.replace(/^#/, '').trim();
  if (fromHash) return fromHash;
  const fromQuery = new URLSearchParams(search).get('t');
  return fromQuery?.trim() || null;
}

/**
 * The hash views the old app used, mapped to the routes that answer them now.
 * A view with no direct equivalent lands on the overview rather than on a
 * not-found page, which is the better failure for a bookmark.
 */
const LEGACY_VIEWS: Record<string, string> = {
  insights: '/user',
  entry: '/user/lessons',
  payments: '/user/lessons',
  students: '/user/students',
  calendar: '/user/calendar',
  profile: '/user/profile',
  backup: '/user/backup',
  premium: '/user/premium',
  receipts: '/user/receipts',
  'receipt-settings': '/user/receipts',
  'receipt-reports': '/user/reports',
  'monthly-report': '/user/reports',
  'student-profile': '/user/students',
  'intake-forms': '/user/payment-documents',
  subscription: '/user/premium',
  upgrade: '/user/premium'
};

export function pathForLegacyView(hash: string): string | null {
  // `#premium/upgrade` was a sub-view; only the first segment identifies it.
  const view = hash.replace(/^#/, '').split('/')[0]?.trim();
  if (!view) return null;
  return LEGACY_VIEWS[view] ?? null;
}
